import { Injectable, inject } from '@angular/core';
import { map, Observable, catchError, throwError, switchMap, timer, takeWhile } from 'rxjs';
import { HttpService } from '../../shared/http-service.class';
import { MyLBApiConfiguration } from '../mylb-api.configuration';
import { IChatMessage, IThread, IAgentResponse } from '../interfaces';

// Foundry Agent API interfaces based on actual API responses
interface IFoundryConversation {
	id: string;
	created_at: number;
}

interface IFoundryResponseRequest {
	agent: {
		type: 'agent_reference';
		name: string;
	};
	conversation?: string;
	input: string;
}

interface IFoundryResponse {
	id: string;
	status: 'in_progress' | 'requires_action' | 'completed' | 'failed';
	output?: any[];
	conversation: {
		id: string;
	};
	created_at?: number;
	error?: {
		message?: string;
	};
}

@Injectable({
	providedIn: 'root'
})
export class AgentService extends HttpService {
	private readonly agentName = 'customer-support';
	private readonly apiVersion = '2025-11-15-preview';
	private readonly pollingInterval = 1000;
	private readonly maxPollingAttempts = 60;
	protected override serviceUrl: string;

	constructor(config: MyLBApiConfiguration) {
		super(config, 'help');
		this.serviceUrl = 'https://mylanguagebird-staging-apim.azure-api.net/help';
	}

	/**
	 * List all threads (conversations)
	 * Note: This would need to be stored/managed in your backend
	 */
	getThreads(): Observable<IThread[]> {
		// TODO: Implement backend endpoint to list user's conversations
		// For now, return empty array - conversations will be created as needed
		return new Observable((observer) => {
			observer.next([]);
			observer.complete();
		});
	}

	/**
	 * List messages in a conversation
	 * Note: Track messages client-side or in your backend
	 */
	getMessages(threadId: string): Observable<IChatMessage[]> {
		// TODO: Implement message history tracking
		return new Observable((observer) => {
			observer.next([]);
			observer.complete();
		});
	}


	/**
	 * Send a message to the agent using HTTP
	 */
	sendMessage(threadId: string, content: string): Observable<IAgentResponse> {
		const request: IFoundryResponseRequest = {
			agent: {
				type: 'agent_reference',
				name: this.agentName
			},
			conversation: threadId,
			input: content
		};

		return this.post<IFoundryResponse>(`${this.serviceUrl}/responses?api-version=${this.apiVersion}`, request).pipe(
			switchMap((response) => {
				console.log('Initial response:', response);

				if (response.status === 'requires_action') {
					console.log('Requires action - auto approving tools');
					return this.handleToolApprovals(response).pipe(
						switchMap(() => {
							console.log('Approval sent, polling for completion');
							return this.pollForCompletion(response.conversation.id, response.id);
						})
					);
				}

				if (response.status === 'in_progress') {
					console.log('In progress - polling for completion');
					return this.pollForCompletion(response.conversation.id, response.id);
				}

				console.log('Response completed immediately');
				return new Observable<IFoundryResponse>((observer) => {
					observer.next(response);
					observer.complete();
				});
			}),
			map((response) => {
				console.log('Final response to map:', response);
				const mapped = this.mapResponseToAgentResponse(response);
				console.log('Mapped response:', mapped);
				return mapped;
			}),
			catchError((error) => {
				console.error('Error sending message:', error);
				return throwError(() => error);
			})
		);
	}

	/**
	 * Handle tool approvals automatically
	 */
	private handleToolApprovals(response: IFoundryResponse): Observable<any> {
		const approvalRequests = response.output?.filter((item: any) => item.type === 'mcp_approval_request') || [];

		if (approvalRequests.length === 0) {
			return new Observable((observer) => {
				observer.next(null);
				observer.complete();
			});
		}

		console.log(`Auto-approving ${approvalRequests.length} tool(s)`);

		const approvals = approvalRequests.map((approval: any) => ({
			type: 'mcp_approval_response',
			id: approval.id,
			approved: true
		}));

		const approvalRequest = {
			agent: {
				type: 'agent_reference',
				name: this.agentName
			},
			conversation: response.conversation.id,
			response_id: response.id,
			output: approvals
		};

		return this.post(`${this.serviceUrl}/responses?api-version=${this.apiVersion}`, approvalRequest);
	}

	/**
	 * Poll for response completion
	 */
	private pollForCompletion(conversationId: string, responseId: string): Observable<IFoundryResponse> {
		let attempts = 0;

		return timer(0, this.pollingInterval).pipe(
			switchMap(() => this.get<IFoundryResponse>(`${this.serviceUrl}/conversations/${conversationId}/responses/${responseId}?api-version=${this.apiVersion}`)),
			takeWhile((response) => {
				attempts++;
				const isProcessing = response.status === 'in_progress' || response.status === 'requires_action';
				const shouldContinue = isProcessing && attempts < this.maxPollingAttempts;

				if (!isProcessing || attempts >= this.maxPollingAttempts) {
					return false;
				}

				return shouldContinue;
			}, true),
			catchError((error) => {
				console.error('Error polling response:', error);
				return throwError(() => error);
			})
		);
	}


	/**
	 * Create a new conversation
	 */
	createThread(): Observable<IThread> {
		return this.post<IFoundryConversation>(`${this.serviceUrl}/conversations?api-version=${this.apiVersion}`, {}).pipe(
			map((conversation) => ({
				id: conversation.id,
				title: 'New Chat',
				lastMessage: '',
				lastMessageDate: new Date(conversation.created_at * 1000),
				unreadCount: 0
			})),
			catchError((error) => {
				console.error('Error creating conversation:', error);
				return throwError(() => error);
			})
		);
	}

	/**
	 * Delete a conversation
	 */
	deleteThread(threadId: string): Observable<void> {
		return new Observable((observer) => {
			observer.next();
			observer.complete();
		});
	}

	/**
	 * Map Foundry Response to IAgentResponse
	 */
	private mapResponseToAgentResponse(response: IFoundryResponse): IAgentResponse {
		let content = '';

		// Extract text content from output array
		if (response.output && Array.isArray(response.output)) {
			const messageOutput = response.output.find((item: any) => item.type === 'message' && item.content);

			if (messageOutput) {
				if (Array.isArray(messageOutput.content)) {
					const textContent = messageOutput.content.find((c: any) => c.type === 'text');
					content = textContent?.text || '';
				} else {
					content = messageOutput.content || '';
				}
			}
		}

		// Fallback messages
		if (!content) {
			if (response.status === 'requires_action') {
				content = 'Processing your request with knowledge base...';
			} else if (response.status === 'in_progress') {
				content = 'Thinking...';
			} else if (response.status === 'failed' && response.error) {
				content = `Error: ${response.error.message || 'Request failed'}`;
			} else {
				content = 'No response available';
			}
		}

		return {
			content: content,
			threadId: response.conversation.id,
			messageId: response.id,
			timestamp: response.created_at ? new Date(response.created_at * 1000) : new Date()
		};
	}
}
